import { Inject, Injectable, InjectionToken } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { IdToken } from '@auth0/auth0-spa-js';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { filter, first, map, mergeMap, switchMap, tap } from 'rxjs/operators';

export interface AuthState {
  token: string | null;
  jwt: IdToken | null;
  scopes: string[];
  isAuthenticated: boolean;
}

export type PhAuthServiceConfig = Observable<{
  CA_REST_API_URL: string;
  AUTH_DOMAIN: string;
  AUTH_AUDIENCE: string;
  AUTH_ORG_ID: string;
  AUTH_DEVICE_CLIENT_ID?: string;
  REFRESH_TOKEN_IN_ADVANCE_SEC: number;
  AUTH_LOGOUT_CALLBACK_URL: string;
  LOCALE: string;
}>;

export const PH_AUTH_SERVICE_CONFIG = new InjectionToken<PhAuthServiceConfig>(
  'PH_AUTH_SERVICE_CONFIG'
);

export enum AuthFlows {
  SDK = '0',
  DEVICE = '1',
}

const initialState: AuthState = {
  token: null,
  jwt: null,
  scopes: [],
  isAuthenticated: false,
};

@Injectable()
export class PhAuthService {
  private state$ = new BehaviorSubject<AuthState>(initialState);
  public token: string | null = null;
  constructor(
    private authSrv: AuthService,
    @Inject(PH_AUTH_SERVICE_CONFIG)
    private phAuthServiceConfig: PhAuthServiceConfig,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.setupTokenRefresh();
  }

  private setupTokenRefresh(): void {
    this.phAuthServiceConfig
      .pipe(
        switchMap((config) => {
          const refreshInAdvance = config.REFRESH_TOKEN_IN_ADVANCE_SEC;
          return this.state$.pipe(
            filter((state) => state.jwt !== null),
            map((state) => ({
              expirationTime: state.jwt!.exp!,
              refreshInAdvance,
            }))
          );
        }),
        map(
          ({ expirationTime, refreshInAdvance }) =>
            expirationTime * 1000 - new Date().getTime() - refreshInAdvance * 1000
        ),
        switchMap((refreshInMs) => timer(refreshInMs)),
        tap(() => this.refreshToken())
      )
      .subscribe();
  }

  private refreshToken(): void {
    this.getAccessTokenSilently().subscribe((token) => {
      this.setToken(token);
    });
  }

  get token$(): Observable<string | null> {
    return this.state$.pipe(map((state) => state.token));
  }

  get jwt$(): Observable<IdToken | null> {
    return this.state$.pipe(map((state) => state.jwt));
  }

  get scopes$(): Observable<string[]> {
    return this.state$.pipe(map((state) => state.scopes));
  }

  get isAuthenticated$(): Observable<boolean> {
    return this.state$.pipe(map((state) => state.isAuthenticated));
  }

  get whenAuthenticated$(): Observable<boolean> {
    return this.isAuthenticated$.pipe(
      filter((isAuthenticated) => isAuthenticated),
      first()
    );
  }

  get user$(): Observable<any> {
    return this.authSrv.isAuthenticated$.pipe(
      filter((sdkAuth) => !!sdkAuth),
      mergeMap(() => this.authSrv.user$)
    );
  }

  setAuthFlow(flow: AuthFlows): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { auth_flow: flow.toString() },
      queryParamsHandling: 'merge',
      skipLocationChange: true,
    });
  }

  setToken(token: string): void {
    try {
      const jwt = JSON.parse(atob(token.split('.')[1]));
      const scopes = jwt.scope.split(' ');
      this.state$.next({
        token,
        jwt,
        scopes,
        isAuthenticated: true,
      });
      this.token = token;
    } catch (error) {
      console.error('Error parsing token:', error);
      this.state$.next(initialState);
    }
  }

  getAccessTokenSilently(): Observable<string> {
    return this.phAuthServiceConfig.pipe(
      switchMap((config) => {
        const organization = config.AUTH_ORG_ID;
        const [ui_locales] = config.LOCALE.split('-');
        return this.authSrv.getAccessTokenSilently({
          authorizationParams: { organization, ui_locales },
        });
      })
    );
  }

  loginWithRedirect(): void {
    this.phAuthServiceConfig.subscribe((config) => {
      const organization = config.AUTH_ORG_ID;
      const [ui_locales] = config.LOCALE.split('-');
      this.authSrv.loginWithRedirect({
        authorizationParams: { organization, ui_locales },
      });
    });
  }

  logout(): void {
    localStorage.removeItem('refreshToken');
    this.phAuthServiceConfig
      .pipe(map((config) => config.AUTH_LOGOUT_CALLBACK_URL))
      .subscribe((returnTo) => {
        this.state$.next(initialState);
        this.authSrv.logout({ logoutParams: { federated: true, returnTo } });
      });
  }
}
